import React, { useEffect, useMemo, useState } from "react";
import { api, VENMO_QR_URL, ZELLE_QR_URL } from "@/lib/api";
import { Heart, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const Tip = () => {
  const [methods, setMethods] = useState([]);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api.get("/payment-methods").then((r) => setMethods(r.data));
  }, []);

  const activeMethods = useMemo(() => methods.filter((m) => m.active), [methods]);
  const localQR = { venmo: VENMO_QR_URL, zelle: ZELLE_QR_URL };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Clipboard API blocked (e.g. inside an iframe / non-HTTPS); not fatal
      // but log it so we can debug later if a user reports the copy doing nothing.
      console.warn("Clipboard copy failed:", err);
    }
  };

  // Venmo: open the app/web. Zelle: no public deep link, so copy the email
  // and tell the user to paste it inside their bank's Zelle.
  const handleAction = async (m, e) => {
    if (m.key === "zelle") {
      e.preventDefault();
      await copy(m.handle);
      setCopied(m.key);
      toast.success("Email copied — paste into your bank app's Zelle", { duration: 4000 });
      setTimeout(() => setCopied(null), 2500);
    }
    // Venmo lets the <a> default behavior open the link
  };

  const renderCard = (m) => {
    const isZelle = m.key === "zelle";
    const href = isZelle ? "#" : m.link;
    const cta = isZelle ? "Copy Zelle Email" : `Open Venmo`;
    let Icon = ExternalLink;
    if (isZelle) {
      Icon = copied === "zelle" ? Check : Copy;
    }
    const note = isZelle
      ? "Tap to copy — then paste in your bank app's Zelle"
      : "Tap to open Venmo with @Retrofit-Seth ready to pay";

    return (
      <a
        key={m.key}
        href={href}
        target={isZelle ? undefined : "_blank"}
        rel={isZelle ? undefined : "noreferrer"}
        onClick={(e) => handleAction(m, e)}
        className="group border border-white/15 hover:border-white p-8 flex flex-col items-center text-center transition-all duration-200 hover:bg-white/[0.03] active:bg-white/10"
        data-testid={`tip-${m.key}`}
      >
        <p className="eyebrow group-hover:text-white">{m.label}</p>

        <div className="my-6 bg-white p-3">
          <img
            src={localQR[m.key]}
            alt={`${m.label} QR`}
            className="w-56 h-56 object-contain"
            data-testid={`tip-qr-${m.key}`}
          />
        </div>

        <p className="font-mono text-sm md:text-base text-white break-all leading-tight">
          {m.handle}
        </p>
        <p className="font-mono text-[10px] text-neutral-500 tracking-[0.1em] mt-3 max-w-[260px] leading-relaxed uppercase">
          {note}
        </p>

        <span
          className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white text-white bg-transparent uppercase tracking-[0.2em] text-[11px] font-mono group-hover:bg-white group-hover:text-black transition-colors"
          data-testid={`tip-action-${m.key}`}
        >
          <Icon size={13} />
          {copied === m.key && isZelle ? "Copied!" : cta}
        </span>
      </a>
    );
  };

  return (
    <div data-testid="tip-page" className="min-h-screen">
      <section className="pt-20 pb-12 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="eyebrow fade-up"><Heart size={12} className="inline mr-2" /> Support the band</p>
          <h1 className="font-heading text-6xl md:text-8xl uppercase leading-[0.9] mt-5 fade-up-1" data-testid="tip-title">
            Tip the Band
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-neutral-300 fade-up-2">
            Every tip keeps the amps on, the strings new and the van running. Scan a QR or tap a card to send.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-6">
          {activeMethods.map(renderCard)}
        </div>

        <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-16 text-center">
          <p className="font-mono text-xs text-neutral-500 tracking-[0.2em] uppercase">
            Thanks for keeping the music alive.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Tip;
